import {useCallback, useMemo} from 'react';
import type {DataLoadStatus} from 'api/types';
import {mapRtkToDataLoad} from 'store/api';
import type {DeletePrizeFunc, Prize, SavePrizeFunc} from '../types';
import {
  useDeletePrizeMutation,
  useGetPrizeQuery,
  usePostPrizeMutation,
  usePutPrizeMutation,
} from '../prizesApi';

export const usePrize = (
  prizeId?: string | null,
  _mock?: boolean,
): {
  prize: DataLoadStatus<Prize>;
  savePrize: SavePrizeFunc;
  deletePrize: DeletePrizeFunc;
} => {
  const queryResult = useGetPrizeQuery(prizeId!, {skip: !prizeId});
  const [postPrize] = usePostPrizeMutation();
  const [putPrize] = usePutPrizeMutation();
  const [deletePrizeMutation] = useDeletePrizeMutation();

  const prize: DataLoadStatus<Prize> = useMemo(
    () =>
      prizeId
        ? mapRtkToDataLoad(
            queryResult.status,
            queryResult.data,
            queryResult.error,
          )
        : {status: 'NotLoaded'},
    [prizeId, queryResult.status, queryResult.data, queryResult.error],
  );

  const handleSavePrize = useCallback<SavePrizeFunc>(
    async prize => {
      const result = prizeId
        ? await putPrize(prize).unwrap()
        : await postPrize(prize).unwrap();
      return {...prize, ...result};
    },
    [prizeId, putPrize, postPrize],
  );

  const handleDeletePrize = useCallback<DeletePrizeFunc>(
    async prizeId => {
      await deletePrizeMutation(prizeId).unwrap();
    },
    [deletePrizeMutation],
  );

  return useMemo(() => {
    return {
      prize,
      savePrize: handleSavePrize,
      deletePrize: handleDeletePrize,
    };
  }, [prize, handleSavePrize, handleDeletePrize]);
};
